import { Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import Attachment from '@components/Attachments/Attachment';
import { ResponsiveGrid } from '@components/ResponsiveGrid';
import { ShareButton } from '@components/ShareButton';

import { useAssessmentResult } from '@hooks/useAssessmentResult';
import { useSession } from '@hooks/useSession';

import { capitalize, formatDate, formatVitalSign } from '@utils/format';

import { ShareType } from 'generated/types';

const messages = defineMessages({
  download: {
    id: 'bhaResults.download',
    defaultMessage: 'Download Report',
  },
  date: {
    id: 'bhaResults.date',
    defaultMessage: 'Assessment Conducted on: {date}',
  },
  disclaimer: {
    id: 'bhaResults.disclaimer',
    defaultMessage:
      'The Digital Health Assessment contains estimations related to potential health risks. It is not intended to diagnose, treat, cure or prevent disease. Please consult your doctor for further interpretation of this report.',
  },
  age: {
    id: 'bhaResults.header.age',
    defaultMessage: 'Age',
  },
  sex: {
    id: 'bhaResults.header.sex',
    defaultMessage: 'Biological Sex',
  },
  smoker: {
    id: 'bhaResults.header.smokerStatus',
    defaultMessage: 'Smoker Status',
  },
  height: {
    id: 'bhaResults.header.height',
    defaultMessage: 'Height',
  },
  weight: {
    id: 'bhaResults.header.weight',
    defaultMessage: 'Weight',
  },
  smokerStatus: {
    id: 'GLOBAL.smokerStatus',
    defaultMessage:
      '{smokerStatus, plural, =0 {Current} one {Former} other {Never}}',
  },
});

interface HeaderProps {
  /**
   * Enables the Share action for this assessment. Only supplied by the
   * authenticated route -- the external shared page passes nothing, so that
   * page stays read-only. The patient is taken from the active session, which
   * is also what the share mutation is authorized against.
   */
  shareContext?: { assessment_id: string } | null;
}

export default function Header({ shareContext = null }: HeaderProps) {
  const { formatMessage } = useIntl();
  const { data, profile } = useAssessmentResult();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id;
  const vitall_patient_id = activeProfile?.vitall_patient_id;

  if (!profile) {
    return null;
  }
  const {
    age,
    height,
    height_denomination,
    sex,
    smoker_status,
    weight,
    weight_denomination,
  } = profile;

  const attachment = data?.attachments.at(0);

  const headerItems = [
    {
      descriptor: messages.age,
      value: age,
    },
    {
      descriptor: messages.height,
      value: formatVitalSign(
        { value: height ?? 0, unit: height_denomination },
        true
      ),
    },
    {
      descriptor: messages.weight,
      value: formatVitalSign(
        { value: weight ?? 0, unit: weight_denomination },
        true
      ),
    },
    {
      descriptor: messages.sex,
      value: capitalize(sex ?? ''),
    },
    {
      descriptor: messages.smoker,
      value: formatMessage(messages.smokerStatus, {
        smokerStatus: smoker_status,
      }),
    },
  ];

  const displayHeaderItems = (
    descriptor: MessageDescriptor,
    value?: string | number | boolean | null
  ) => (
    <Stack my={1}>
      <Typography color="text.secondary" variant="body2">
        {formatMessage(descriptor)}
      </Typography>

      <Typography>{value}</Typography>
    </Stack>
  );

  return (
    <>
      <Grid2 container justifyContent="flex-end">
        <Stack spacing={1} alignItems="flex-end">
          {shareContext && vitall_patient_id && (
            <ShareButton
              color="primary.main"
              assessment_id={shareContext.assessment_id}
              share_type={ShareType.DigitalHealthAssessment}
              vitall_patient_id={vitall_patient_id}
            />
          )}
          {attachment && patient_id && (
            <Attachment
              attachment={attachment}
              patient_id={patient_id}
              render={({ attachment, handleDownload, index }) => {
                return (
                  <Button onClick={() => handleDownload(attachment, index)}>
                    {formatMessage(messages.download)}
                  </Button>
                );
              }}
            />
          )}
          <Typography color="text.secondary">
            {formatMessage(messages.date, {
              date: formatDate({ date: data?.assessmentDate }),
            })}
          </Typography>
        </Stack>
      </Grid2>

      <Typography
        color="text.secondary"
        sx={{ textAlign: 'center' }}
        variant="body2"
      >
        {formatMessage(messages.disclaimer)}
      </Typography>

      <Paper>
        <ResponsiveGrid
          items={headerItems.map(({ descriptor, value }) =>
            displayHeaderItems(descriptor, value)
          )}
        />
      </Paper>
    </>
  );
}
